/**
 * PATH: apps/qa/src/lib/offline/syncOperations.ts
 * PURPOSE: Sync operations for uploading files and syncing dirty data
 */

import { IDBPDatabase } from 'idb'
import { API_BASE } from '@quotech/api'
import { useAuthStore } from '@quotech/auth'
import type { PendingUpload, CachedInspection, QAOfflineDB, MAX_UPLOAD_ATTEMPTS } from './types'
import {
  assetUploadForm,
  photoEvidenceBody,
  QA_EVIDENCE_PATH,
  QDRIVE_UPLOAD_PATH,
} from '../evidence/evidencePayload'
import { shouldFlushUpload } from './uploadOwnerGate'

const getAuthHeader = () => `Bearer ${localStorage.getItem('quotech-auth-token')}`

function currentSessionOwner() {
  const state = useAuthStore.getState()
  return {
    userId: state.user?.id ?? null,
    organisationId: state.organisationId ?? null,
  }
}

export async function uploadFile(upload: PendingUpload): Promise<void> {
  // /api/qa/evidence/upload/ was never a registered route, so every queued
  // photo 404'd until it exhausted its attempts and was dropped. Same two-leg
  // contract as the live camera: bytes to QDrive, then link the asset id.
  const assetResponse = await fetch(`${API_BASE}/api${QDRIVE_UPLOAD_PATH}`, {
    method: 'POST',
    body: assetUploadForm(upload.file, upload.fileName),
    headers: { 'Authorization': getAuthHeader() },
  })
  if (!assetResponse.ok) {
    throw new Error(`Upload failed: ${assetResponse.status} ${assetResponse.statusText}`)
  }
  const assetId = (await assetResponse.json())?.id
  if (!assetId) throw new Error('Upload returned no asset id')

  const evidenceResponse = await fetch(`${API_BASE}/api${QA_EVIDENCE_PATH}`, {
    method: 'POST',
    headers: { 'Authorization': getAuthHeader(), 'Content-Type': 'application/json' },
    body: JSON.stringify(
      photoEvidenceBody({
        target: {
          type: upload.entityType,
          id: upload.entityId,
          inspectionId: upload.metadata?.inspectionId,
        },
        assetId,
        noteText: upload.metadata?.notes,
        deviceMetadata: upload.metadata?.gps,
      }),
    ),
  })
  if (!evidenceResponse.ok) {
    throw new Error(
      `Evidence link failed: ${evidenceResponse.status} ${evidenceResponse.statusText}`,
    )
  }
}

export async function processPendingUploads(
  db: IDBPDatabase<QAOfflineDB>,
  maxAttempts: number
): Promise<{ synced: number; failed: number }> {
  let synced = 0, failed = 0
  const uploads = await db.getAll('pendingUploads')
  const session = currentSessionOwner()

  for (const upload of uploads) {
    if (!shouldFlushUpload(upload, session)) {
      // Drop unowned / cross-session rows rather than flush under the wrong identity.
      await db.delete('pendingUploads', upload.id)
      failed++
      continue
    }
    if (upload.attempts >= maxAttempts) continue
    try {
      await uploadFile(upload)
      await db.delete('pendingUploads', upload.id)
      synced++
    } catch (error) {
      await db.put('pendingUploads', {
        ...upload, attempts: upload.attempts + 1, lastAttempt: new Date(),
        error: error instanceof Error ? error.message : 'Unknown error',
      })
      failed++
    }
  }
  return { synced, failed }
}

export async function syncDirtyInspections(
  db: IDBPDatabase<QAOfflineDB>,
  addConflict: (c: { entityType: string; entityId: string; localData: unknown; serverData: unknown }) => Promise<string>
): Promise<{ synced: number; failed: number; conflicts: number }> {
  let synced = 0, failed = 0, conflicts = 0
  // Booleans are not valid IDB keys — filter in memory instead of using the index
  const allInspections = await db.getAll('inspections')
  const dirtyInspections = allInspections.filter(i => i.isDirty)

  for (const inspection of dirtyInspections) {
    try {
      const serverResponse = await fetch(`${API_BASE}/api/qa/inspections/${inspection.id}/`, { headers: { 'Authorization': getAuthHeader() } })
      if (!serverResponse.ok) throw new Error(`Failed to fetch: ${serverResponse.status}`)
      const serverData = await serverResponse.json()

      if (new Date(serverData.updated_at) > inspection.syncedAt) {
        await addConflict({ entityType: 'inspection', entityId: inspection.id, localData: inspection.data, serverData })
        conflicts++; continue
      }

      const pushResponse = await fetch(`${API_BASE}/api/qa/inspections/${inspection.id}/`, {
        method: 'PATCH', headers: { 'Authorization': getAuthHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify(inspection.data),
      })
      if (!pushResponse.ok) throw new Error(`Failed to push: ${pushResponse.status}`)

      await db.put('inspections', { ...inspection, syncedAt: new Date(), isDirty: false })
      synced++
    } catch (error) {
      console.error(`[OfflineStore] Failed to sync inspection ${inspection.id}:`, error)
      failed++
    }
  }
  return { synced, failed, conflicts }
}

export async function syncDirtyChecklistItems(
  db: IDBPDatabase<QAOfflineDB>
): Promise<{ synced: number; failed: number; conflicts: number }> {
  let synced = 0, failed = 0, conflicts = 0
  const allItems = await db.getAll('checklistItems')
  const dirtyItems = allItems.filter(i => i.isDirty)

  for (const item of dirtyItems) {
    try {
      const response = await fetch(`${API_BASE}/api/qa/checklist-items/${item.id}/set-status/`, {
        method: 'POST', headers: { 'Authorization': getAuthHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: item.status, ...item.data }),
      })
      if (!response.ok) throw new Error(`Failed to sync: ${response.status}`)
      await db.put('checklistItems', { ...item, syncedAt: new Date(), isDirty: false })
      synced++
    } catch (error) {
      console.error(`[OfflineStore] Failed to sync item ${item.id}:`, error)
      failed++
    }
  }
  return { synced, failed, conflicts }
}
